(() => {
  const elementName = 'pdp-v2-accordion-item';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2AccordionItem extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.trigger = this.querySelector('[data-pdp-v2-accordion-trigger]');
      this.panel = this.querySelector('[data-pdp-v2-accordion-panel]');
      this.handleClick = this.handleClick.bind(this);

      if (!this.trigger || !this.panel) {
        return;
      }

      this.trigger.addEventListener('click', this.handleClick);
      const startsExpanded = this.trigger.getAttribute('aria-expanded') === 'true';

      this.setExpanded(startsExpanded, false);

      if (startsExpanded) {
        this.closeFaqSiblings(false);
      }
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.handleClick);
      }

      if (this.panel && this.handleTransitionEnd) {
        this.panel.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      this.initialized = false;
    }

    handleClick() {
      const isExpanded = this.trigger.getAttribute('aria-expanded') === 'true';

      if (!isExpanded) {
        this.closeFaqSiblings(true);
      } else if (this.closest('.pdp-v2-faq__list')) {
        return;
      }

      this.setExpanded(!isExpanded, true);
    }

    closeFaqSiblings(animate) {
      const faqList = this.closest('.pdp-v2-faq__list');

      if (!faqList) {
        return;
      }

      faqList.querySelectorAll('pdp-v2-accordion-item').forEach((item) => {
        if (item !== this && typeof item.setExpanded === 'function') {
          item.setExpanded(false, animate);
        }
      });
    }

    setExpanded(expanded, animate) {
      if (!this.trigger || !this.panel) {
        return;
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.trigger.setAttribute('aria-expanded', String(expanded));
      this.classList.toggle('pdp-v2-accordion__item--open', expanded);

      if (!animate || prefersReducedMotion) {
        this.panel.hidden = !expanded;
        this.panel.style.height = expanded ? 'auto' : '0px';
        this.panel.style.opacity = expanded ? '1' : '0';
        return;
      }

      if (this.handleTransitionEnd) {
        this.panel.removeEventListener('transitionend', this.handleTransitionEnd);
      }

      if (expanded) {
        this.panel.hidden = false;
      }

      const startHeight = expanded ? 0 : this.panel.scrollHeight;
      const endHeight = expanded ? this.panel.scrollHeight : 0;

      this.panel.style.height = `${startHeight}px`;
      this.panel.style.opacity = expanded ? '0' : '1';

      void this.panel.offsetHeight;
      this.panel.style.height = `${endHeight}px`;
      this.panel.style.opacity = expanded ? '1' : '0';

      this.handleTransitionEnd = (event) => {
        if (event.target !== this.panel || event.propertyName !== 'height') {
          return;
        }

        this.panel.removeEventListener('transitionend', this.handleTransitionEnd);
        this.handleTransitionEnd = null;

        if (expanded) {
          this.panel.style.height = 'auto';
          this.panel.style.opacity = '1';
        } else {
          this.panel.hidden = true;
          this.panel.style.height = '0px';
        }
      };

      this.panel.addEventListener('transitionend', this.handleTransitionEnd);
    }
  }

  customElements.define(elementName, PdpV2AccordionItem);
})();

(() => {
  const elementName = 'pdp-v2-behind-carousel';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2BehindCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.retryCount = 0;
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.handleViewportChange = this.handleViewportChange.bind(this);

      if (this.mobileMedia.addEventListener) {
        this.mobileMedia.addEventListener('change', this.handleViewportChange);
      } else if (this.mobileMedia.addListener) {
        this.mobileMedia.addListener(this.handleViewportChange);
      }

      this.handleViewportChange();
    }

    disconnectedCallback() {
      window.clearTimeout(this.retryTimer);

      if (this.mobileMedia) {
        if (this.mobileMedia.removeEventListener) {
          this.mobileMedia.removeEventListener('change', this.handleViewportChange);
        } else if (this.mobileMedia.removeListener) {
          this.mobileMedia.removeListener(this.handleViewportChange);
        }
      }

      this.destroySwiper();
      this.mobileMedia = null;
      this.initialized = false;
    }

    handleViewportChange() {
      window.clearTimeout(this.retryTimer);

      if (this.mobileMedia && this.mobileMedia.matches) {
        this.initializeSwiper();
        return;
      }

      this.destroySwiper();
    }

    initializeSwiper() {
      if (this.carouselSwiper && !this.carouselSwiper.destroyed) {
        return;
      }

      if (this.getBoundingClientRect().width <= 0 || !window.Swiper) {
        this.retryWhenSwiperLoads();
        return;
      }

      const pagination = this.querySelector('[data-pdp-v2-behind-pagination]');

      this.carouselSwiper = new window.Swiper(this, {
        slidesPerView: 1,
        speed: 450,
        watchOverflow: true,
        allowTouchMove: true,
        grabCursor: true,
        loop: false,
        autoplay: false,
        observer: true,
        observeParents: true,
        resizeObserver: true,
        pagination: {
          el: pagination,
          clickable: true,
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
          containerMessage: 'Behind every pair features',
          paginationBulletMessage: 'Go to feature {{index}}',
        },
      });
    }

    destroySwiper() {
      this.retryCount = 0;

      if (!this.carouselSwiper || this.carouselSwiper.destroyed) {
        this.carouselSwiper = null;
        return;
      }

      this.carouselSwiper.destroy(true, true);
      this.carouselSwiper = null;
    }

    retryWhenSwiperLoads() {
      window.clearTimeout(this.retryTimer);

      if (!this.mobileMedia || !this.mobileMedia.matches || this.retryCount >= 60) {
        return;
      }

      this.retryCount += 1;
      this.retryTimer = window.setTimeout(() => this.initializeSwiper(), 100);
    }
  }

  customElements.define(elementName, PdpV2BehindCarousel);
})();

(() => {
  const elementName = 'pdp-v2-made-tabs';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2FeatureTabs extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-pdp-v2-made-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-pdp-v2-made-panel]'));
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleClick);
        tab.addEventListener('keydown', this.handleKeydown);
      });

      const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

      if (selectedTab) {
        this.activateTab(selectedTab, false);
      }
    }

    disconnectedCallback() {
      if (!this.tabs) {
        return;
      }

      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.handleClick);
        tab.removeEventListener('keydown', this.handleKeydown);
      });

      this.tabs = null;
      this.panels = null;
      this.initialized = false;
    }

    handleClick(event) {
      this.activateTab(event.currentTarget, false);
    }

    handleKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);

      if (currentIndex === -1) {
        return;
      }

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[nextIndex], true);
    }

    activateTab(selectedTab, shouldFocus) {
      if (!selectedTab) {
        return;
      }

      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      });

      const selectedPanelKey = selectedTab.getAttribute('data-pdp-v2-made-tab');
      const selectedPanelId = selectedTab.getAttribute('aria-controls');
      const selectedPanel = this.panels.find((panel) => {
        if (selectedPanelKey && panel.getAttribute('data-pdp-v2-made-panel') === selectedPanelKey) {
          return true;
        }

        return selectedPanelId && panel.id === selectedPanelId;
      });

      if (selectedPanel) {
        this.panels.forEach((panel) => {
          const isSelected = panel === selectedPanel;

          panel.hidden = !isSelected;

          if (isSelected && selectedTab.id) {
            panel.setAttribute('aria-labelledby', selectedTab.id);
          }
        });
      }

      if (shouldFocus) {
        selectedTab.focus();
      }
    }
  }

  customElements.define(elementName, PdpV2FeatureTabs);
})();

(() => {
  const elementName = 'pdp-v2-reviews';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2Reviews extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.track = this.querySelector('[data-pdp-v2-review-track]');
      this.refreshCards();
      this.previousButton = this.querySelector('[data-pdp-v2-review-prev]');
      this.nextButton = this.querySelector('[data-pdp-v2-review-next]');
      this.viewport = this.querySelector('.pdp-v2-reviews__viewport');
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);

      if (!this.cards.length) {
        return;
      }

      const activeCard = this.cards.find((card) => card.getAttribute('data-pdp-v2-review-active') === 'true');
      this.activeIndex = activeCard ? this.cards.indexOf(activeCard) : Math.floor(this.cards.length / 2);

      if (this.previousButton) {
        this.previousButton.addEventListener('click', this.handlePrevious);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.handleNext);
      }

      this.updateCards(false);
      this.loadYotpoReviews();
    }

    disconnectedCallback() {
      if (this.yotpoFetchController) {
        this.yotpoFetchController.abort();
        this.yotpoFetchController = null;
      }

      if (this.previousButton) {
        this.previousButton.removeEventListener('click', this.handlePrevious);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.handleNext);
      }

      this.cards = null;
      this.track = null;
      this.previousButton = null;
      this.nextButton = null;
      this.viewport = null;
      this.initialized = false;
    }

    handlePrevious() {
      this.setActiveIndex(this.activeIndex - 1, true);
    }

    handleNext() {
      this.setActiveIndex(this.activeIndex + 1, true);
    }

    setActiveIndex(index, shouldScroll) {
      const totalCards = this.cards ? this.cards.length : 0;

      if (!totalCards) {
        return;
      }

      this.activeIndex = (index + totalCards) % totalCards;
      this.updateCards(shouldScroll);
    }

    updateCards(shouldScroll) {
      if (!this.cards || !this.cards.length) {
        return;
      }

      const totalCards = this.cards.length;
      const halfVisible = Math.floor(totalCards / 2);
      let activeCard = null;

      this.cards.forEach((card, index) => {
        let offset = index - this.activeIndex;

        if (offset > halfVisible) {
          offset -= totalCards;
        } else if (offset < -halfVisible) {
          offset += totalCards;
        }

        const position = this.getPositionName(offset);

        card.style.order = String(offset + halfVisible + 1);
        card.setAttribute('data-pdp-v2-review-position', position);
        card.toggleAttribute('data-pdp-v2-review-active', offset === 0);
        card.setAttribute('aria-current', offset === 0 ? 'true' : 'false');

        if (offset === 0) {
          activeCard = card;
        }
      });

      if (shouldScroll && activeCard && this.viewport && window.matchMedia('(max-width: 749px)').matches) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        activeCard.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }

    refreshCards() {
      this.cards = Array.from(this.querySelectorAll('[data-pdp-v2-review-card]'));
    }

    async loadYotpoReviews() {
      const appKey = (this.dataset.yotpoAppKey || '').trim();
      const productId = (this.dataset.yotpoProductId || '').trim();
      const limit = Math.min(Math.max(Number.parseInt(this.dataset.yotpoLimit, 10) || 5, 3), 10);

      if (!appKey || !productId || !this.track) {
        return;
      }

      this.yotpoFetchController = new AbortController();

      try {
        const requestedCount = Math.max(limit, 10);
        const endpoint = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=${requestedCount}&page=1`;
        const response = await fetch(endpoint, {
          headers: {
            Accept: 'application/json',
          },
          signal: this.yotpoFetchController.signal,
        });

        if (!response.ok) {
          return;
        }

        const payload = await response.json();
        const reviews = this.normalizeYotpoReviews(payload, limit);

        if (!reviews.length || !this.isConnected) {
          return;
        }

        this.renderYotpoReviews(reviews);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Unable to load PDP v2 Yotpo reviews.', error);
        }
      } finally {
        this.yotpoFetchController = null;
      }
    }

    normalizeYotpoReviews(payload, limit) {
      const reviews = payload?.response?.reviews;

      if (!Array.isArray(reviews)) {
        return [];
      }

      return reviews
        .filter((review) => (
          review
          && !review.deleted
          && review.verified_buyer === true
          && review.content
          && Math.round(Number(review.score)) === 5
        ))
        .slice(0, limit)
        .map((review) => ({
          content: this.normalizeReviewText(review.content),
          name: this.normalizeReviewText(review.user?.display_name) || 'Groov customer',
        }))
        .filter((review) => review.content);
    }

    normalizeReviewText(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    renderYotpoReviews(reviews) {
      const verifiedIconSrc = this.querySelector('.pdp-v2-reviews__verified img')?.getAttribute('src') || '';
      const verifiedLabel = this.querySelector('.pdp-v2-reviews__verified span')?.textContent.trim() || 'Verified Buyer';
      const starsSrc = this.querySelector('.pdp-v2-reviews__stars')?.getAttribute('src') || '';
      const starsAlt = this.querySelector('.pdp-v2-reviews__stars')?.getAttribute('alt') || '5 star review';
      const fragment = document.createDocumentFragment();

      reviews.forEach((review) => {
        const card = document.createElement('article');
        card.className = 'pdp-v2-reviews__card';
        card.setAttribute('data-pdp-v2-review-card', '');

        const meta = document.createElement('div');
        meta.className = 'pdp-v2-reviews__meta';

        const name = document.createElement('p');
        name.className = 'pdp-v2-reviews__name';
        name.textContent = review.name;
        meta.append(name);

        const verified = document.createElement('div');
        verified.className = 'pdp-v2-reviews__verified';

        if (verifiedIconSrc) {
          const verifiedIcon = document.createElement('img');
          verifiedIcon.src = verifiedIconSrc;
          verifiedIcon.alt = '';
          verifiedIcon.width = 24;
          verifiedIcon.height = 24;
          verifiedIcon.loading = 'lazy';
          verifiedIcon.setAttribute('aria-hidden', 'true');
          verified.append(verifiedIcon);
        }

        const verifiedText = document.createElement('span');
        verifiedText.textContent = verifiedLabel;
        verified.append(verifiedText);
        meta.append(verified);
        card.append(meta);

        if (starsSrc) {
          const stars = document.createElement('img');
          stars.className = 'pdp-v2-reviews__stars';
          stars.src = starsSrc;
          stars.alt = starsAlt;
          stars.width = 93;
          stars.height = 17;
          stars.loading = 'lazy';
          card.append(stars);
        }

        const quote = document.createElement('blockquote');
        quote.className = 'pdp-v2-reviews__quote';
        quote.textContent = `"${review.content}"`;
        card.append(quote);

        fragment.append(card);
      });

      this.track.replaceChildren(fragment);
      this.refreshCards();
      this.activeIndex = 0;
      this.updateCards(false);
    }

    getPositionName(offset) {
      if (offset === -2) {
        return 'far-left';
      }

      if (offset === -1) {
        return 'near-left';
      }

      if (offset === 0) {
        return 'center';
      }

      if (offset === 1) {
        return 'near-right';
      }

      return 'far-right';
    }
  }

  customElements.define(elementName, PdpV2Reviews);
})();

(() => {
  const elementName = 'pdp-v2-angle-showcase';

  if (customElements.get(elementName)) {
    return;
  }

  class PdpV2AngleShowcase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-pdp-v2-angle-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-pdp-v2-angle-panel]'));
      this.floatingImages = Array.from(this.querySelectorAll('[data-pdp-v2-angle-float]'));
      this.floatingLayer = this.querySelector('.pdp-v2-angle__floating-layer');
      this.floatingAnimationStyle = this.dataset.pdpV2AngleFloatingAnimation || 'style-2';
      this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMarqueeMedia = window.matchMedia('(max-width: 749px)');
      this.mobileMarqueeSpeed = 0.16;
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.updateParallax = this.updateParallax.bind(this);
      this.queuedParallaxUpdate = this.queuedParallaxUpdate.bind(this);
      this.updateMobileMarquee = this.updateMobileMarquee.bind(this);
      this.pauseMobileMarquee = this.pauseMobileMarquee.bind(this);
      this.scheduleMobileMarqueeResume = this.scheduleMobileMarqueeResume.bind(this);
      this.rafId = null;
      this.mobileMarqueeRafId = null;
      this.mobileMarqueeLastTimestamp = null;
      this.mobileMarqueePaused = false;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.handleTabClick);
        tab.addEventListener('keydown', this.handleTabKeydown);
      });

      if (this.floatingLayer) {
        this.floatingLayer.addEventListener('pointerdown', this.pauseMobileMarquee);
        this.floatingLayer.addEventListener('pointerup', this.scheduleMobileMarqueeResume);
        this.floatingLayer.addEventListener('pointercancel', this.scheduleMobileMarqueeResume);
        this.floatingLayer.addEventListener('touchstart', this.pauseMobileMarquee, { passive: true });
        this.floatingLayer.addEventListener('touchend', this.scheduleMobileMarqueeResume);
        this.floatingLayer.addEventListener('wheel', this.scheduleMobileMarqueeResume, { passive: true });
      }

      const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

      if (selectedTab) {
        this.activateTab(selectedTab, false);
      }

      window.addEventListener('scroll', this.queuedParallaxUpdate, { passive: true });
      window.addEventListener('resize', this.handleViewportChange);
      if (this.mobileMarqueeMedia.addEventListener) {
        this.mobileMarqueeMedia.addEventListener('change', this.handleViewportChange);
      } else if (this.mobileMarqueeMedia.addListener) {
        this.mobileMarqueeMedia.addListener(this.handleViewportChange);
      }
      this.handleViewportChange();
    }

    disconnectedCallback() {
      if (this.tabs) {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.handleTabClick);
          tab.removeEventListener('keydown', this.handleTabKeydown);
        });
      }

      window.removeEventListener('scroll', this.queuedParallaxUpdate);
      window.removeEventListener('resize', this.handleViewportChange);

      if (this.mobileMarqueeMedia) {
        if (this.mobileMarqueeMedia.removeEventListener) {
          this.mobileMarqueeMedia.removeEventListener('change', this.handleViewportChange);
        } else if (this.mobileMarqueeMedia.removeListener) {
          this.mobileMarqueeMedia.removeListener(this.handleViewportChange);
        }
      }

      if (this.floatingLayer) {
        this.floatingLayer.removeEventListener('pointerdown', this.pauseMobileMarquee);
        this.floatingLayer.removeEventListener('pointerup', this.scheduleMobileMarqueeResume);
        this.floatingLayer.removeEventListener('pointercancel', this.scheduleMobileMarqueeResume);
        this.floatingLayer.removeEventListener('touchstart', this.pauseMobileMarquee);
        this.floatingLayer.removeEventListener('touchend', this.scheduleMobileMarqueeResume);
        this.floatingLayer.removeEventListener('wheel', this.scheduleMobileMarqueeResume);
      }

      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
      }

      this.stopMobileMarquee();
      window.clearTimeout(this.mobileMarqueeResumeTimer);

      this.tabs = null;
      this.panels = null;
      this.floatingImages = null;
      this.floatingLayer = null;
      this.floatingAnimationStyle = null;
      this.reducedMotionMedia = null;
      this.mobileMarqueeMedia = null;
      this.initialized = false;
    }

    handleTabClick(event) {
      this.activateTab(event.currentTarget, false);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);

      if (currentIndex === -1) {
        return;
      }

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this.tabs.length;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      this.activateTab(this.tabs[nextIndex], true);
    }

    activateTab(selectedTab, shouldFocus) {
      if (!selectedTab) {
        return;
      }

      const selectedPanelKey = selectedTab.getAttribute('data-pdp-v2-angle-tab');

      this.tabs.forEach((tab) => {
        const isSelected = tab === selectedTab;

        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isSelected = panel.getAttribute('data-pdp-v2-angle-panel') === selectedPanelKey;

        panel.hidden = !isSelected;

        if (isSelected && selectedTab.id) {
          panel.setAttribute('aria-labelledby', selectedTab.id);
        }
      });

      if (shouldFocus) {
        selectedTab.focus();
      }
    }

    handleViewportChange() {
      if (this.reducedMotionMedia && this.reducedMotionMedia.matches) {
        this.resetParallax();
        this.stopMobileMarquee();
        return;
      }

      if (this.isMobileMarqueeMode()) {
        this.resetParallax();
        this.startMobileMarquee();
        return;
      }

      this.stopMobileMarquee();
      this.queuedParallaxUpdate();
    }

    queuedParallaxUpdate() {
      if (this.rafId) {
        return;
      }

      this.rafId = window.requestAnimationFrame(this.updateParallax);
    }

    updateParallax() {
      this.rafId = null;

      if (!this.floatingImages || !this.floatingImages.length) {
        return;
      }

      if (this.isMobileMarqueeMode()) {
        this.resetParallax();
        return;
      }

      if (this.reducedMotionMedia && this.reducedMotionMedia.matches) {
        this.resetParallax();
        return;
      }

      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const scrollableDistance = Math.max(rect.height - viewportHeight, 1);
      const progress = Math.min(Math.max(-rect.top / scrollableDistance, 0), 1);

      this.floatingImages.forEach((image) => {
        const speed = Number(image.dataset.pdpV2AngleSpeed) || 0;
        const rotation = Number(image.dataset.pdpV2AngleRotate) || 0;

        image.style.setProperty('--pdp-v2-angle-scroll-y', `${progress * speed}px`);
        image.style.setProperty('--pdp-v2-angle-scroll-rotate', `${progress * rotation}deg`);
        this.updateFloatingFade(image, viewportHeight);
      });
    }

    updateFloatingFade(image, viewportHeight) {
      if (this.floatingAnimationStyle === 'style-1') {
        image.style.setProperty('--pdp-v2-angle-fade-opacity', '1');
        return;
      }

      const rect = image.getBoundingClientRect();
      let opacity = 1;

      if (this.floatingAnimationStyle === 'style-3') {
        const fadeStart = viewportHeight * 0.62;
        const fadeEnd = viewportHeight * 0.18;
        opacity = Math.min(Math.max((rect.top - fadeEnd) / (fadeStart - fadeEnd), 0), 1);
      } else {
        const fadeStart = viewportHeight * 0.88;
        const fadeEnd = viewportHeight * 0.58;
        opacity = Math.min(Math.max((fadeStart - rect.top) / (fadeStart - fadeEnd), 0), 1);
      }

      image.style.setProperty('--pdp-v2-angle-fade-opacity', opacity.toFixed(3));
    }

    resetParallax() {
      if (!this.floatingImages) {
        return;
      }

      this.floatingImages.forEach((image) => {
        image.style.setProperty('--pdp-v2-angle-scroll-y', '0px');
        image.style.setProperty('--pdp-v2-angle-scroll-rotate', '0deg');
        image.style.setProperty('--pdp-v2-angle-fade-opacity', '1');
      });
    }

    isMobileMarqueeMode() {
      if (!this.floatingLayer) {
        return false;
      }

      const floatingLayerStyles = window.getComputedStyle(this.floatingLayer);

      return floatingLayerStyles.display === 'flex'
        && ['auto', 'scroll'].includes(floatingLayerStyles.overflowX);
    }

    startMobileMarquee() {
      if (!this.floatingLayer || this.mobileMarqueeRafId || !this.isMobileMarqueeMode()) {
        return;
      }

      this.mobileMarqueeLastTimestamp = null;
      this.mobileMarqueeRafId = window.requestAnimationFrame(this.updateMobileMarquee);
    }

    stopMobileMarquee() {
      if (this.mobileMarqueeRafId) {
        window.cancelAnimationFrame(this.mobileMarqueeRafId);
      }

      this.mobileMarqueeRafId = null;
      this.mobileMarqueeLastTimestamp = null;
    }

    pauseMobileMarquee() {
      this.mobileMarqueePaused = true;
      window.clearTimeout(this.mobileMarqueeResumeTimer);
    }

    scheduleMobileMarqueeResume() {
      window.clearTimeout(this.mobileMarqueeResumeTimer);

      if (!this.isMobileMarqueeMode()) {
        return;
      }

      this.mobileMarqueeResumeTimer = window.setTimeout(() => {
        this.mobileMarqueePaused = false;
        this.startMobileMarquee();
      }, 700);
    }

    updateMobileMarquee(timestamp) {
      this.mobileMarqueeRafId = null;

      if (!this.floatingLayer || !this.isMobileMarqueeMode()) {
        return;
      }

      if (this.reducedMotionMedia && this.reducedMotionMedia.matches) {
        return;
      }

      if (this.mobileMarqueeLastTimestamp === null) {
        this.mobileMarqueeLastTimestamp = timestamp;
      }

      const delta = Math.min(timestamp - this.mobileMarqueeLastTimestamp, 64);
      const firstItem = this.floatingLayer.querySelector('.pdp-v2-angle__float:not(.pdp-v2-angle__float--clone)');
      const firstClone = this.floatingLayer.querySelector('.pdp-v2-angle__float--clone');
      const resetPoint = firstItem && firstClone
        ? Math.max(firstClone.offsetLeft - firstItem.offsetLeft, 1)
        : Math.max(this.floatingLayer.scrollWidth / 2, 1);

      this.mobileMarqueeLastTimestamp = timestamp;

      if (!this.mobileMarqueePaused && this.floatingLayer.scrollWidth > this.floatingLayer.clientWidth) {
        this.floatingLayer.scrollLeft += delta * this.mobileMarqueeSpeed;

        while (this.floatingLayer.scrollLeft >= resetPoint) {
          this.floatingLayer.scrollLeft -= resetPoint;
        }
      }

      this.mobileMarqueeRafId = window.requestAnimationFrame(this.updateMobileMarquee);
    }
  }

  customElements.define(elementName, PdpV2AngleShowcase);
})();
